import {BaseLoadingStore} from "app/modules/common/stores/BaseLoadingStore";
import {action, makeObservable, observable, runInAction} from "mobx";
import {FieldState, FormState} from "formstate";
import {email, int, required, requiredNumber} from "app/modules/common/form/validators";
import {inject} from "react-ioc";
import {ApiClients} from "app/modules/company/services/AxiosBaseClient";
import {MainStore} from "app/modules/company/stores/MainStore";
import {
    CompanyIntegrationEditModel,
    CompanyIntegrationType,
    NamedValue,
    TenantClaims
} from "app/modules/company/api/clients.api";
import {CacheService} from "app/modules/company/services/CacheService";
import {apiBaseUrl} from "app/modules/company/services/AxiosBaseClient";
import PubSub from "pubsub-js";
import {Events} from "app/modules/common/const/EventNames";

export class CompanyIntegrationDetailsStore extends BaseLoadingStore {
    @inject companyApiClients: ApiClients;
    @inject mainStore: MainStore;
    @inject cacheService: CacheService;

    title: string = 'New Integration';
    integrationId: number;
    isActive: boolean = true;
    eldDrivers: NamedValue[] = [];
    eldVehicles: NamedValue[] = [];

    formState = new FormState({
        name: new FieldState('').validators(required),
        type: new FieldState<CompanyIntegrationType>(CompanyIntegrationType.Sylectus).validators(requiredNumber, int)
    });

    formSmtp = new FormState({
        address: new FieldState('').validators(required),
        user: new FieldState('').validators(required),
        password: new FieldState('').validators(required),
        port: new FieldState<number>(null).validators(required, int),
    });

    formSylectus = new FormState({
        corporationId: new FieldState('').validators(required),
        corporationPassword: new FieldState('').validators(required),
        userId: new FieldState('').validators(required),
        userPassword: new FieldState('').validators(required),
    });

    formDAT = new FormState({
        corporationEmail: new FieldState('').validators(required, email),
        corporationPassword: new FieldState('').validators(required),
    });

    formInbox = new FormState({
        fullName: new FieldState('').validators(required),
        email: new FieldState('').validators(required, email),
        password: new FieldState('').validators(required),
        type: new FieldState('').validators(required),
        forwardEmail: new FieldState('').validators(required, email),
    });

    formQBO = new FormState({
        environmentType: new FieldState<number>(1).validators(requiredNumber, int),
        billsAccountId: new FieldState(''),
        invoicesAccountId: new FieldState(''),
    });

    lastQboSettingsParsed: Record<string, unknown> | null = null;
    hasQboRefreshToken: boolean = false;
    qboAccounts: { id: string; name: string }[] = [];
    qboAccountsError: string = '';

    tenantVerifiedDomains: string[] = [];

    isReadOnlyMode: boolean = false;
    isRedirectToNewEntity: boolean = false;

    constructor() {
        super();

        this.isReadOnlyMode = !this.mainStore.checkClaim(TenantClaims.IntegrationEdit);

        makeObservable(this, {
            title: observable,
            isActive: observable,
            isRedirectToNewEntity: observable,
            lastQboSettingsParsed: observable,
            hasQboRefreshToken: observable,
            qboAccounts: observable,
            qboAccountsError: observable,
            isLoadingQboAccounts: observable,
            tenantVerifiedDomains: observable,

            init: action,
            activate: action,
            deactivate: action,
            save: action,
            refreshQBOToken: action,
            loadQboAccounts: action
        });
    }

    save = async () => {
        await this.formState.validate();

        if(this.formState.hasError) {
            return;
        }

        let settings = {} as any;

        if(this.formState.$.type.value == CompanyIntegrationType.Sylectus) {
            await this.formSylectus.validate();

            if(this.formSylectus.hasError) {
                return;
            }

            settings.CorporationId = +this.formSylectus.$.corporationId.value;
            settings.CorporationPassword = this.formSylectus.$.corporationPassword.value;
            settings.UserId = +this.formSylectus.$.userId.value;
            settings.UserPassword = this.formSylectus.$.userPassword.value;
        }

        if(this.formState.$.type.value == CompanyIntegrationType.DAT) {
            await this.formDAT.validate();

            if(this.formDAT.hasError) {
                return;
            }

            settings.Email = this.formDAT.$.corporationEmail.value;
            settings.Password = this.formDAT.$.corporationPassword.value;
        }

        if(this.formState.$.type.value == CompanyIntegrationType.SMTP) {
            await this.formSmtp.validate();

            if(this.formSmtp.hasError) {
                return;
            }

            settings.Address = this.formSmtp.$.address.value;
            settings.User = this.formSmtp.$.user.value;
            settings.Password = this.formSmtp.$.password.value;
            settings.Port = +this.formSmtp.$.port.value;
        }

        if(this.formState.$.type.value == CompanyIntegrationType.Inbox) {
            await this.formInbox.validate();

            if(this.formInbox.hasError) {
                return;
            }

            settings.FullName = this.formInbox.$.fullName.value;
            settings.Email = this.formInbox.$.email.value;
            settings.Password = this.formInbox.$.password.value;
            settings.Type = this.formInbox.$.type.value;
            settings.ForwardEmail = this.formInbox.$.forwardEmail.value;
        }

        if(this.formState.$.type.value == CompanyIntegrationType.QBO) {
            await this.formQBO.$.environmentType.validate();
            if(this.formQBO.$.environmentType.hasError) return;

            const base = this.lastQboSettingsParsed != null ? { ...this.lastQboSettingsParsed } : {};
            const bills = (this.formQBO.$.billsAccountId.value || '').trim();
            const invoices = (this.formQBO.$.invoicesAccountId.value || '').trim();
            settings = {
                ...base,
                environmentType: this.formQBO.$.environmentType.value,
                billsAccountId: bills || undefined,
                invoicesAccountId: invoices || undefined
            } as any;
        }

        if(this.formState.$.type.value == CompanyIntegrationType.VerifiedSmtpSender) {
            if (this.integrationId === 0 && (!this.tenantVerifiedDomains || this.tenantVerifiedDomains.length === 0)) {
                PubSub.publish(Events.Notification, "Verified SMTP Sender requires at least one verified domain. Configure verified domains in Admin → Tenant details first.");
                return;
            }
            settings = {};
        }

        await this.wrapLoadingApiCall(async () => {
            this.integrationId = await this.companyApiClients.companyIntegrationClient.upsert(new CompanyIntegrationEditModel({
                id: this.integrationId,
                name: this.formState.$.name.value,
                type: this.formState.$.type.value,
                active: this.isActive,
                settingsJSON: JSON.stringify(settings)
            }));

            runInAction(() => {
                if(this.integrationId > 0) {
                    this.isSavedSuccessfully = true;
                } else {
                    this.isRedirectToNewEntity = true;
                }
            });
        });
    }

    activate = async () => {
        this.wrapLoadingApiCall(async () => {
            await this.companyApiClients.companyIntegrationClient.activate(this.integrationId);
            runInAction(() => {
                this.isActive = true;
            });
        });
    }

    deactivate = async () => {
        this.wrapLoadingApiCall(async () => {
            await this.companyApiClients.companyIntegrationClient.deactivate(this.integrationId);
            runInAction(() => {
                this.isActive = false;
            });
        });
    }

    getAuthorizeQBOUrl = () => apiBaseUrl + "/company/CompanyIntegration/AuthorizeQBO/" + this.integrationId;

    refreshQBOToken = async () => {
        await this.wrapLoadingApiCall(async () => {
            try {
                await this.companyApiClients.axiosInstance.post('/company/CompanyIntegration/RefreshQBOToken/' + this.integrationId);
                PubSub.publish(Events.Notification, "Token refreshed successfully.");
            } catch (e: any) {
                PubSub.publish(Events.OrderTaskNotification, "Token refresh failed. Re-authorization may be required.");
            }
        });
    };

    isLoadingQboAccounts: boolean = false;

    loadQboAccounts = async () => {
        if (this.integrationId <= 0 || this.isLoadingQboAccounts) return;
        runInAction(() => { this.qboAccountsError = ''; });
        this.isLoadingQboAccounts = true;
        try {
            const res = await this.companyApiClients.axiosInstance.get('/company/CompanyIntegration/GetQBOAccounts/' + this.integrationId);
            const list = (res?.status === 200 && Array.isArray(res?.data)) ? res.data : [];
            runInAction(() => {
                this.qboAccounts = list.filter((x: any) => x && (x.Id ?? x.id)).map((x: any) => ({ id: String(x.Id ?? x.id), name: String(x.Name ?? x.name ?? x.Id ?? x.id ?? '') }));
                this.qboAccountsError = '';
            });
        } catch {
            runInAction(() => {
                this.qboAccounts = [];
                this.qboAccountsError = 'Could not load accounts. Try Reconnect or Reload accounts.';
            });
        } finally {
            runInAction(() => { this.isLoadingQboAccounts = false; });
        }
    };

    init = async (id: number | string | undefined) => {
        if (id === 'new' || id === '' || id == null) {
            this.integrationId = 0;
        } else if (typeof id === 'number') {
            this.integrationId = isNaN(id) ? 0 : id;
        } else {
            const n = parseInt(id, 10);
            this.integrationId = isNaN(n) ? 0 : n;
        }

        await this.wrapInitLoadingApiCall(async () => {
            const isNew = this.integrationId === 0;
            this.tenantVerifiedDomains = await this.companyApiClients.tenantDataProviderClient.getVerifiedDomains() || [];
            if (isNew) {
                this.formInbox.$.forwardEmail.value = await this.companyApiClients.companyIntegrationClient.generateForwardEmail();
            }

            if(this.integrationId > 0) {
                this.qboAccounts = [];
                const result = await this.companyApiClients.companyIntegrationClient.getById(this.integrationId);

                const rawType = result.type ?? (result as any)?.Type ?? (result as any)?.type ?? (result as any)?.integrationType ?? (result as any)?.IntegrationType;
                let typeVal: number | undefined = rawType != null ? (typeof rawType === 'number' ? rawType : parseInt(String(rawType), 10)) : undefined;
                if ((typeVal == null || isNaN(typeVal)) && typeof rawType === 'string' && /^qbo$/i.test(rawType)) typeVal = CompanyIntegrationType.QBO;
                const resolvedType = (typeVal != null && !isNaN(typeVal)) ? typeVal : rawType;

                const validTypes = [CompanyIntegrationType.Sylectus, CompanyIntegrationType.SMTP, CompanyIntegrationType.DAT, CompanyIntegrationType.Inbox, CompanyIntegrationType.QBO, CompanyIntegrationType.VerifiedSmtpSender];
                runInAction(() => {
                    this.title = result.name ?? '';
                    this.isActive = result.active ?? false;
                    this.formState.$.name.value = result.name ?? '';
                    if (resolvedType != null && validTypes.includes(resolvedType as CompanyIntegrationType))
                        this.formState.$.type.value = resolvedType;
                });

                let s = result.settingsJSON ?? (result as any)?.settingsJSON;
                const settingsStr = typeof s === 'string' ? s : (s != null ? JSON.stringify(s) : '{}');
                const settings = (() => { try { return JSON.parse(settingsStr || '{}') || {}; } catch { return {}; } })();

                if(this.formState.$.type.value == CompanyIntegrationType.QBO) {
                    this.lastQboSettingsParsed = settings;
                    this.hasQboRefreshToken = !!(settings.RefreshToken || settings.refreshToken);
                    const et = settings.EnvironmentType ?? settings.environmentType;
                    if (et != null) {
                        const num = typeof et === 'number' ? et : parseInt(et, 10);
                        if (!isNaN(num)) this.formQBO.$.environmentType.value = num;
                    }
                    const bid = settings.BillsAccountId ?? settings.billsAccountId;
                    if (bid != null) this.formQBO.$.billsAccountId.value = String(bid);
                    const iid = settings.InvoicesAccountId ?? settings.invoicesAccountId;
                    if (iid != null) this.formQBO.$.invoicesAccountId.value = String(iid);
                    try {
                        this.qboAccountsError = '';
                        const res = await this.companyApiClients.axiosInstance.get('/company/CompanyIntegration/GetQBOAccounts/' + this.integrationId);
                        const list = (res?.status === 200 && Array.isArray(res?.data)) ? res.data : [];
                        runInAction(() => {
                            this.qboAccounts = list.filter((x: any) => x && (x.Id ?? x.id)).map((x: any) => ({ id: String(x.Id ?? x.id), name: String(x.Name ?? x.name ?? x.Id ?? x.id ?? '') }));
                            this.qboAccountsError = '';
                        });
                    } catch {
                        runInAction(() => {
                            this.qboAccounts = [];
                            this.qboAccountsError = 'Could not load accounts. Try Reconnect or Reload accounts.';
                        });
                    }
                }

                if(this.formState.$.type.value == CompanyIntegrationType.SMTP) {
                    this.formSmtp.$.address.value = settings.Address;
                    this.formSmtp.$.user.value = settings.User;
                    this.formSmtp.$.password.value = settings.Password;
                    this.formSmtp.$.port.value = settings.Port;
                }

                if(this.formState.$.type.value == CompanyIntegrationType.Sylectus) {
                    this.formSylectus.$.corporationId.value = settings.CorporationId;
                    this.formSylectus.$.corporationPassword.value = settings.CorporationPassword;
                    this.formSylectus.$.userId.value = settings.UserId;
                    this.formSylectus.$.userPassword.value = settings.UserPassword;
                }

                if(this.formState.$.type.value == CompanyIntegrationType.DAT) {
                    this.formDAT.$.corporationEmail.value = settings.Email;
                    this.formDAT.$.corporationPassword.value = settings.Password;
                }

                if(this.formState.$.type.value == CompanyIntegrationType.Inbox) {
                    this.formInbox.$.fullName.value = settings.FullName;
                    this.formInbox.$.email.value = settings.Email;
                    this.formInbox.$.password.value = settings.Password;
                    this.formInbox.$.type.value = settings.Type;

                    if(settings.ForwardEmail?.length > 0) {
                        this.formInbox.$.forwardEmail.value = settings.ForwardEmail;
                    }
                }
            }
        });
    }
}
